import {
    VirtualTourImageView,
    VirtualTourMinimap,
    VirtualView,
    VRTourPointerFilter,
    VRTourViewFilter,
} from '@features/virtual-tour/types/view';
import createStore from '@utilities/store';

interface VirtualTourStateItem {
    viewStack: string[];
    views: VirtualView[];
    currentView?: VirtualView;
    minimap?: VirtualTourMinimap;
}

export interface VirtualTourState {
    [id: string]: VirtualTourStateItem;
}

const ADD_VIRTUAL_TOUR_DATA = 'addVirtualTourData';
const UPDATE_VIRTUAL_TOUR_DATA = 'updateVirtualTourData';
const PUSH_VIEW = 'pushView';
const POP_VIEW = 'popView';

const initialState: VirtualTourState = {};

const getLastView = (tour: VirtualTourStateItem) => {
    const viewId = tour.viewStack[tour.viewStack.length - 1];
    const view = tour.views.find((v) => v.id === viewId);

    return view;
};

const actions = {
    [ADD_VIRTUAL_TOUR_DATA]: (
        state: VirtualTourState,
        data: {
            tourId: string;
            views: VirtualView[];
            viewId?: string;
            minimap?: VirtualTourMinimap;
        },
    ) => {
        const { tourId, views, viewId, minimap } = data;
        const tour: VirtualTourStateItem = { views, viewStack: [viewId || views[0].id], minimap };
        tour.currentView = getLastView(tour);

        state[tourId] = tour;
        return state;
    },
    [UPDATE_VIRTUAL_TOUR_DATA]: (
        state: VirtualTourState,
        data: {
            tourId: string;
            views: VirtualView[];
            viewId?: string;
            minimap?: VirtualTourMinimap;
        },
    ) => {
        const { tourId, views: newViews, viewId, minimap } = data;
        const filteredViews = newViews.filter(
            ({ id }) => !state[tourId].views.find((v) => v.id === id),
        );
        const tour: VirtualTourStateItem = {
            views: [...state[tourId].views, ...filteredViews],
            viewStack:
                viewId && viewId !== state[tourId].currentView?.id
                    ? [...state[tourId].viewStack, viewId]
                    : state[tourId].viewStack,
            minimap,
        };
        tour.currentView = getLastView(tour);

        state[tourId] = tour;
        return state;
    },
    [PUSH_VIEW]: (
        state: VirtualTourState,
        { tourId, viewId }: { tourId: string; viewId: string },
    ) => {
        const tour = { ...state[tourId] };
        tour.viewStack = [...tour.viewStack, viewId];
        tour.currentView = getLastView(tour);

        state[tourId] = tour;
        return state;
    },
    [POP_VIEW]: (state: VirtualTourState, tourId: string) => {
        const tour = { ...state[tourId] };
        tour.viewStack = tour.viewStack.slice(0, -1);
        tour.currentView = getLastView(tour);

        state[tourId] = tour;
        return state;
    },
};

export const pushView = (tourId: string, viewId: string) => {
    virtualTourStore.dispatch(PUSH_VIEW, { tourId, viewId });
};

export const popView = (tourId: string) => {
    virtualTourStore.dispatch(POP_VIEW, tourId);
};

export const setData = (
    tourId: string,
    data: { views: VirtualView[]; firstViewId?: string | null; minimap?: VirtualTourMinimap },
) => {
    const { views, firstViewId, minimap } = data;
    const action = virtualTourStore.getState()[tourId]
        ? UPDATE_VIRTUAL_TOUR_DATA
        : ADD_VIRTUAL_TOUR_DATA;
    virtualTourStore.dispatch(action, {
        tourId,
        views,
        viewId: firstViewId,
        minimap,
    });
};

export const getViewInStore = (tourId: string, viewId: string) => {
    const vrState = virtualTourStore.getState();
    return vrState[tourId] && vrState[tourId].views.find((v) => v.id === viewId);
};

export const findPointerInCurrentView = (tourId: string, filter: VRTourPointerFilter) => {
    const currentView = virtualTourStore.getState()[tourId].currentView as VirtualTourImageView;
    if (currentView?.type !== '2d') return;
    return currentView.pointers.find(filter);
};

export const findView = (tourId: string, filter: VRTourViewFilter) => {
    const views = virtualTourStore.getState()[tourId].views;
    return views.find(filter);
};

export const virtualTourStore = createStore<VirtualTourState>({ initialState, actions });
